import type React from 'react';
import { forwardRef } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
  IconButton,
  Typography,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';

import { PrimaryButton, TransparentButton, DangerButton } from 'app/shared-components/buttons';
import Close2Icon from 'app/assets/icons/close-2.svg?react';

type ActionType = 'primary' | 'danger';

type ConfirmModalProps = {
  open: boolean;
  handleClose: (
    e: React.MouseEvent<HTMLButtonElement>,
    reason?: 'backdropClick' | 'escapeKeyDown'
  ) => void;
  title: string;
  icon?: React.ReactNode;
  content?: string;
  children?: React.ReactNode;
  confirmHandler: (e: React.MouseEvent<HTMLButtonElement>) => void;
  confirmType: ActionType;
  confirmLabel?: string;
  disabled?: boolean;
  confirmDisabled?: boolean;
};

const buttonsByConfirmType: Record<ActionType, React.ComponentType<any>> = {
  primary: PrimaryButton,
  danger: DangerButton,
};

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ConfirmModal({
  open,
  handleClose,
  title,
  icon,
  confirmHandler,
  confirmType,
  confirmLabel = 'Confirm',
  content,
  disabled = false,
  confirmDisabled = false,
  children,
}: ConfirmModalProps): React.ReactElement {
  const ConfirmButton = buttonsByConfirmType[confirmType];

  return (
    <HealtheraStyledDialog open={open} handleClose={handleClose}>
      <DialogTitle className="border-b border-border flex justify-between items-center">
        <div className="flex items-center gap-8">
          {icon}
          <Typography className="font-700 text-16 text-typography-primary">{title}</Typography>
        </div>
        <IconButton onClick={handleClose}>
          <Close2Icon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description" component="div" className="text-14">
          {content || children}
        </DialogContentText>
      </DialogContent>
      <DialogActions disableSpacing sx={{ padding: '8px 20px 16px', gap: '12px' }}>
        <TransparentButton label="Cancel" cb={handleClose} disabled={disabled} />
        <ConfirmButton
          label={confirmLabel}
          cb={confirmHandler}
          disabled={disabled || confirmDisabled}
        />
      </DialogActions>
    </HealtheraStyledDialog>
  );
}

function HealtheraStyledDialog({
  open,
  handleClose,
  children,
}: {
  open: boolean;
  handleClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
      sx={{
        // Rendered by ConfirmProvider above the page, so lift it over dialogs that open it
        zIndex: (theme) => theme.zIndex.modal + 1,
        '& .MuiDialog-paper': {
          borderRadius: '4px',
        },
        '& .MuiDialogContent-root': {
          padding: '20px',
        },
      }}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      {children}
    </Dialog>
  );
}
